// src/components/search/SearchModal.jsx
import React, { useState, useEffect, Fragment } from "react";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Link as RouterLink } from "react-router-dom";
import { useLocale } from "../../contexts/LocaleContext"; // 假設用 LocaleContext 獲取 locale
import { searchAll } from "../../api/apiService"; // search endpoint

const SearchIcon = ({ className = "w-5 h-5" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
  >
    <path
      fillRule="evenodd"
      d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
      clipRule="evenodd"
    />
  </svg>
);

const SearchModal = ({ isOpen, onClose }) => {
  const { locale } = useLocale();
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const getExcerpt = (text, q, len = 120) => {
    if (!text) return "";
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text.slice(0, len) + (text.length > len ? "..." : "");
    const start = Math.max(0, idx - Math.floor(len / 2));
    let excerpt = text.slice(start, start + len);
    if (start > 0) excerpt = "..." + excerpt;
    if (start + len < text.length) excerpt = excerpt + "...";
    return excerpt;
  };

  const doSearch = async (term) => {
    const q = term?.trim();
    if (!q) {
      setResults([]);
      setHasSearched(false);
      return;
    }
    setLoading(true);
    setError("");
    setHasSearched(true);
    try {
      const data = await searchAll(q, "aqutek-asia", locale);
      const arr = Array.isArray(data) ? data : data?.data ?? data?.results ?? [];
      // console.log("[SearchModal] Parsed array:", arr[1].hits);
      
      const normalized_products = arr[0].hits.map((item) => {
        // Extract text from fearures_content - safely handle nested structure
        let textContent = "";
        try {
          const parent = item.fearures_content?.[0]?.children?.[0]?.children?.[0];
          if (parent?.text) {
            textContent = parent.text;
          }
        } catch (e) {
          console.warn("[SearchModal] Error extracting text from product item:", e);
        }
      
        // Use getExcerpt to limit text length if needed
        const excerpt = textContent ? getExcerpt(textContent, q, 150) : "";

        // Clean name: remove newlines and extra whitespace
        let cleanName = item.banner?.title || "";
        if (cleanName && typeof cleanName === "string") {
          // Remove both literal "\n" string and actual newline characters
          cleanName = cleanName
            .replace(/\\n/g, " ")      // Literal "\n" string
            .trim();                   // Remove leading/trailing spaces
        } else {
          cleanName = "";
        }

        return {
          name: cleanName,
          url: "/products/" + item.name,
          text: excerpt
        };
      });

      console.log("[SearchModal] Final normalized results:", normalized_products);
      

      const normalized_infra = arr[1].hits.map((item) => {
        // Extract text from fearures_content - safely handle nested structure
        let textContent = "";
        try {
          const parent = item.content?.[0]?.children?.[0];
          if (parent?.text) {
            textContent = parent.text;
          }
        } catch (e) {
          console.warn("[SearchModal] Error extracting text from infra item:", e);
        }
      
        // Use getExcerpt to limit text length if needed
        const excerpt = textContent ? getExcerpt(textContent, q, 150) : "";
        
        return {
          name: item.title,
          url: "/infra#" + item.title,
          text: excerpt
        };
      });

      console.log("[SearchModal] Final normalized results:", normalized_infra);

      const normalized = [...normalized_products, ...normalized_infra];
      
      
      setResults(normalized);
    } catch (err) {
      console.error("[SearchModal] Search error:", err);
      console.error("[SearchModal] Error details:", {
        message: err?.message,
        response: err?.response?.data,
        status: err?.response?.status
      });
      setError(err?.response?.data?.message || err?.message || "Failed to fetch search results.");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    doSearch(searchTerm);
  };


  // 當 Modal 關閉時，清空搜索詞同結果 (可選)
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
      setResults([]);
      setError("");
      setHasSearched(false);
    }
  }, [isOpen]);


  const HighlightedText = ({ text, highlight }) => {
    if (!highlight || !text) {
      return <>{text}</>;
    }
    const parts = text.split(new RegExp(`(${highlight})`, "gi"));
    return (
      <>
        {parts.map((part, i) =>
          part.toLowerCase() === highlight.toLowerCase() ? (
            <mark key={i} className="bg-yellow-200 px-0.5 rounded-sm">
              {part}
            </mark>
          ) : (
            part
          )
        )}
      </>
    );
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[99]" onClose={onClose}>
        {" "}
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </TransitionChild>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 pt-10 md:pt-20">
            {" "}
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-2xl lg:max-w-3xl transform overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl transition-all">
                <form
                  onSubmit={handleSearch}
                  className="p-6 border-b border-gray-200"
                >
                  <label htmlFor="modal-search-input" className="sr-only">
                    Search
                  </label>
                  <div className="flex rounded-md shadow-sm border border-gray-300">
                    <div className="relative flex-grow focus-within:z-10">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SearchIcon
                          className="h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                      </div>
                      <input
                        type="text"
                        name="modal-search"
                        id="modal-search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSearch(e);
                          }
                        }}
                        className="block w-full rounded-none rounded-l-md pl-10 py-3 sm:text-sm"
                        placeholder=""
                        autoFocus
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="relative -ml-px inline-flex items-center space-x-2 rounded-r-md border border-gray-150 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
                    >
                      <SearchIcon
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                      <span>Search</span>
                    </button>
                  </div>
                </form>

                <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                  {" "}
                  {loading && (
                    <p className="text-center text-gray-500 py-4">
                      {/* Searching... */}
                    </p>
                  )}
                  {error && (
                    <p className="text-center text-red-500 py-4">
                      Error: {error}
                    </p>
                  )}
                  {!loading &&
                    !error &&
                    hasSearched &&
                    results.length === 0 && (
                      <p className="text-center text-gray-500 py-4">
                        {/* No results found for "{searchTerm}". */}
                      </p>
                    )}
                  {!loading && !error && !hasSearched && (
                    <p className="text-center text-gray-400 py-4">
                      {/* Enter a term above and click Search. */}
                    </p>
                  )}
                  {results.length > 0 && (
                    <ul className="space-y-4">
                      {results.map((result) => (
                        <li
                          key={result.id || result.url}
                          className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0"
                        >
                        
                          <RouterLink
                            to={result.url || "#"}
                            className="group block hover:bg-gray-50 p-2 rounded-md"
                            onClick={onClose}
                          >
                            <h4 className="text-md font-semibold text-blue-600 group-hover:text-blue-800 mb-0.5">
                              <HighlightedText
                                text={result.name || "Untitled"}
                                highlight={searchTerm}
                              />
                            </h4>
                            {result.text && (
                              <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                                <HighlightedText
                                  text={result.text}
                                  highlight={searchTerm}
                                />
                              </p>
                            )}
                          </RouterLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SearchModal;
